import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/modal.jsx';
import { ChannelSettingsFields, DEFAULT_CHANNEL_VALUES } from './channel-settings-fields.jsx';
import { ApiError } from '../../api/index.js';

const PATH_RE = /^\/[a-z0-9_-]+(\/[a-z0-9_-]+)*$/;

/**
 * Channel create / edit modal used on the Channels page.
 *
 *   • CREATE — always custom (cross-unit ad-hoc). OSS channels are
 *     created inline from the unit-detail panel on the OSS page.
 *   • EDIT   — comes here for BOTH custom AND OSS channels. The
 *     `isOss` flag is derived from initial.source so the form
 *     adapts: OSS edits get the Home node only checkbox + a
 *     read-only path field; custom edits keep the editable path
 *     and skip home_node_only (server refuses it on custom anyway).
 */
export function ChannelFormModal({ open, mode, initial, onClose, onSave }) {
  const isCreate = mode === 'create';
  // CREATE here is always custom. EDIT inherits the source from initial.
  const isOss = !isCreate && initial?.source === 'oss';
  const [values, setValues] = useState({ ...DEFAULT_CHANNEL_VALUES, path: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setBusy(false);
    setError(null);
    if (isCreate) {
      setValues({ ...DEFAULT_CHANNEL_VALUES, path: '/custom/' });
    } else if (initial) {
      setValues({
        name: initial.name || '',
        path: initial.path || '',
        type: initial.type || 'chat',
        scope: initial.scope || 'direct',
        priority: initial.priority || 'routine',
        retention: { ...initial.retention } || { textDays: null, audioDays: null, imageDays: null, filesDays: null },
        description: initial.description || '',
        // hot-fix #10 — pull the current values so the toggles reflect
        // the saved state on each open.
        homeNodeOnly: !!initial.homeNodeOnly,
        archived:     !!initial.archived,
        // hot-fix #11 — server returns CSV-parsed array; default to ['text']
        // for legacy rows that lack the column.
        dataTypes: Array.isArray(initial.dataTypes) && initial.dataTypes.length > 0
          ? initial.dataTypes.slice()
          : ['text'],
        // hot-fix #12 — channel-wide retention (days). NULL = unlimited.
        chatRetentionDays: initial.chatRetentionDays ?? null,
        // 2026-06-19 — PoC settings. Default mode to 'simplex' for
        // edit-mode rehydration if the row hasn't set one yet.
        pocMode: initial.pocMode || 'simplex',
        pocRecording: !!initial.pocRecording,
        pocPttMaxSec: initial.pocPttMaxSec ?? 180,
      });
    }
  }, [open, isCreate, initial]);

  const errors = useMemo(() => {
    const e = {};
    if (!values.name.trim()) e.name = 'Name is required.';
    if (values.name.length > 128) e.name = 'Max 128 characters.';
    if (isCreate || (initial && values.path !== initial.path)) {
      if (!PATH_RE.test(values.path)) {
        e.path = 'Path must look like /seg/seg/seg, lowercase a-z 0-9 with - or _';
      }
    }
    return e;
  }, [values, isCreate, initial]);

  const canSubmit = Object.keys(errors).length === 0 && !busy;

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const payload = {
        name: values.name.trim(),
        type: values.type,
        priority: values.priority,
        retention: values.retention,
        description: values.description.trim() || null,
        // hot-fix #11
        dataTypes: Array.isArray(values.dataTypes) ? values.dataTypes : ['text'],
        // hot-fix #12 — only send when chat-category active. Server
        // ignores it for map-category channels anyway, but explicit is
        // cleaner.
        chatRetentionDays: values.chatRetentionDays ?? null,
        // 2026-06-19 — PoC settings. Server auto-derives pocMode=null
        // when no poc_* token is in dataTypes, but we ship the
        // operator's choice so round-trip stable.
        pocMode: values.pocMode || 'simplex',
        pocRecording: !!values.pocRecording,
        pocPttMaxSec: values.pocPttMaxSec ?? null,
      };
      // home_node_only — OSS-only on the wire. ChannelSettingsFields
      // hides the toggle for custom (isOss=false), but be defensive on
      // the payload too: server refuses it on custom and we don't want
      // to surface that as a confusing error if a stale value lingers
      // in `values` from a previous OSS edit.
      if (isOss) payload.homeNodeOnly = !!values.homeNodeOnly;
      // archived — edit-only (the field is hidden during CREATE).
      if (!isCreate) payload.archived = !!values.archived;
      if (isCreate) payload.path = values.path.trim();
      // OSS channels can't rename their path — server rejects, and the
      // form locks the field below. So only thread path through for
      // custom edits.
      else if (initial && !isOss && values.path !== initial.path) payload.path = values.path.trim();
      await onSave(payload);
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message || 'Operation failed.');
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={
        isCreate
          ? 'Create custom channel'
          : `Edit ${initial?.name || 'channel'}${isOss ? ' (OSS)' : ''}`
      }
      description={
        isCreate
          ? 'Ad-hoc channel for cross-unit nets (operations, MEDEVAC, fires). Members are added explicitly later.'
          : isOss
            ? 'OSS channel — derived from a unit. Path is fixed; settings, retention, and roaming policy are editable here.'
            : 'Custom channel. Path, settings, retention, and metadata are all editable.'
      }
      size="lg"
      footer={
        <>
          <button type="button" onClick={onClose} disabled={busy} className="btn-secondary" data-skip-autofocus>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={!canSubmit} className="btn-primary">
            {busy ? (
              <>
                <span className="loader-spinner" aria-hidden="true" /> Saving…
              </>
            ) : (
              <>
                <i className={`fa-solid ${isCreate ? 'fa-plus' : 'fa-floppy-disk'}`} aria-hidden="true" />
                {isCreate ? 'Create channel' : 'Save changes'}
              </>
            )}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error && (
          <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-soft px-4 py-3 text-sm text-rose-700">
            <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="ch-name" className="field-label">Display name</label>
            <input
              id="ch-name"
              className="field"
              value={values.name}
              onChange={(e) => setValues((p) => ({ ...p, name: e.target.value }))}
              autoComplete="off"
              maxLength={128}
              disabled={busy}
              placeholder="e.g. Operation Thunder Control"
            />
            {errors.name && <p className="mt-1 text-xs text-rose-600">{errors.name}</p>}
          </div>
          <div>
            <label htmlFor="ch-path" className="field-label">
              Path {isOss && <span className="font-normal text-mist">(read-only — derived from unit)</span>}
            </label>
            <input
              id="ch-path"
              className={`field font-mono text-[13px] ${isOss ? 'cursor-not-allowed opacity-70' : ''}`}
              value={values.path}
              onChange={(e) => setValues((p) => ({ ...p, path: e.target.value }))}
              autoComplete="off"
              spellCheck="false"
              disabled={busy || isOss}
              placeholder="/ops/operation-thunder/control"
            />
            {errors.path && <p className="mt-1 text-xs text-rose-600">{errors.path}</p>}
          </div>
        </div>

        <ChannelSettingsFields
          values={values}
          errors={errors}
          onChange={setValues}
          isOss={isOss}
          isEdit={!isCreate}
          busy={busy}
        />
      </form>
    </Modal>
  );
}
